import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/truncated-text";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { BetaAccessGate } from "@/components/beta-access-gate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, UserCircle, Building2, Mail, Phone, MapPin, Pencil, Trash2, FolderOpen,
  ChevronDown, ChevronUp, Send, ShieldOff, Clock, MessageSquare, MoreHorizontal, Home,
  Search, ArrowUp, ArrowDown, ChevronsUpDown,
} from "lucide-react";
import type { Owner, PropertyGroup, OwnerBillingOverride } from "@shared/schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertOwnerSchema } from "@shared/schema";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import { DateRangeFilter, computeDateRange, getPresetLabel, type DatePreset } from "@/components/DateRangeFilter";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

type OwnerFinancials = {
  revenue: number;
  expenses: number;
  netIncome: number;
  propertyCount: number;
};

type GroupProperty = { id: number; name: string; address: string | null };

type OwnersResponse = {
  owners: Owner[];
  groupsByOwner: Record<number, PropertyGroup[]>;
  financialsByOwner: Record<number, OwnerFinancials>;
  unreadByOwner: Record<number, number>;
  groupPropertiesByGroup: Record<number, GroupProperty[]>;
};

type OwnerMessage = {
  id: number;
  ownerId: number;
  userId: number;
  senderRole: "owner" | "pm";
  // Who wrote it — a co-owner's name on JV threads (set by the merged-thread API).
  senderName: string | null;
  body: string;
  ownerReadAt: string | null;
  pmReadAt: string | null;
  createdAt: string;
};

function formatMessageTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (days === 1) return "Yesterday " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

type SingleOwnerResponse = {
  owner: Owner;
  groups: PropertyGroup[];
  billingOverride: OwnerBillingOverride | null;
};

// portalStatus / portalShowDraftInvoices are managed by the invite flow, not
// this form — omit them so zod's .default() can't inject "not_invited" into an
// edit payload and silently revoke an active owner's portal access.
const ownerFormSchema = insertOwnerSchema.omit({ userId: true, portalStatus: true, portalShowDraftInvoices: true });
type OwnerFormValues = z.infer<typeof ownerFormSchema>;

const PORTAL_STATUS_LABELS: Record<string, string> = {
  not_invited: "Not Invited",
  invited: "Invited",
  active: "Active",
  inactive: "Inactive",
};
const PORTAL_STATUS_COLORS: Record<string, string> = {
  not_invited: "secondary",
  invited: "outline",
  active: "default",
  inactive: "destructive",
};

function formatLastLogin(value: Owner["lastLoginAt"]): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function OwnerMessagesDialog({
  owner,
  open,
  onClose,
}: {
  owner: Owner | null;
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [body, setBody] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const ownerId = owner?.id;

  const { data: messages, isLoading } = useQuery<OwnerMessage[]>({
    queryKey: ["/api/app/owners", ownerId, "messages"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/app/owners/${ownerId}/messages`);
      return res.json();
    },
    enabled: open && !!ownerId,
    refetchInterval: open ? 15000 : false,
  });

  const sendMutation = useMutation({
    mutationFn: async (msgBody: string) =>
      apiRequest("POST", `/api/app/owners/${ownerId}/messages`, { body: msgBody }),
    onSuccess: () => {
      setBody("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/owners", ownerId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
    },
    onError: (err) => {
      const message = parseErrorMessage(err, "Please try again.");
      toast({
        title: message === "Invite owner to send message" ? message : "Failed to send message",
        description: message === "Invite owner to send message"
          ? "The owner must activate their portal account before messages can be sent. Your draft has been kept."
          : message,
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (open && messages) {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
    }
  }, [open, messages]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const handleSend = () => {
    const trimmed = body.trim();
    if (!trimmed || sendMutation.isPending) return;
    if (owner?.portalStatus !== "active") {
      toast({
        title: "Invite owner to send message",
        description: "The owner must activate their portal account before messages can be sent. Your draft has been kept.",
        variant: "destructive",
      });
      return;
    }
    sendMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sendMutation.isPending && !v) onClose(); }}>
      <DialogContent
        className="max-w-lg"
        onInteractOutside={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-brand" />
            Messages with {owner?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col">
          <div className="flex-1 overflow-y-auto p-1 space-y-3" style={{ maxHeight: 380, minHeight: 280 }} data-testid="owner-message-thread">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className={`h-14 rounded-xl ${i % 2 === 0 ? "w-3/4 ml-auto" : "w-3/4"}`} />
                ))}
              </div>
            ) : !messages?.length ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <MessageSquare className="h-12 w-12 text-gray-200 mb-3" />
                <p className="text-gray-500 text-sm font-medium">No messages yet</p>
                <p className="text-gray-400 text-xs mt-1">Start the conversation with {owner?.name} below.</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isPm = msg.senderRole === "pm";
                return (
                  <div key={msg.id} className={`flex ${isPm ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                        isPm
                          ? "bg-brand text-white rounded-br-sm"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm"
                      }`}
                      data-testid={`message-bubble-${msg.id}`}
                    >
                      {/* JV threads merge co-owners' messages — label which owner wrote it */}
                      {!isPm && msg.senderName && (
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5" data-testid={`message-sender-${msg.id}`}>
                          {msg.senderName}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                      <p className={`text-xs mt-1 ${isPm ? "text-blue-200" : "text-gray-400"}`}>
                        {formatMessageTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-gray-100 dark:border-gray-800 pt-3 mt-2 flex gap-2 items-end">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message… (Ctrl+Enter to send)"
              className="resize-none text-sm"
              rows={2}
              maxLength={4000}
              data-testid="input-owner-message-body"
              disabled={sendMutation.isPending}
            />
            <Button
              onClick={handleSend}
              disabled={!body.trim() || sendMutation.isPending}
              className="h-10 w-10 p-0 shrink-0"
              data-testid="button-send-owner-message"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OwnerForm({
  open,
  onClose,
  owner,
  allGroups,
  initialGroupIds,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  owner?: Owner | null;
  allGroups: PropertyGroup[];
  initialGroupIds: number[];
  /** Fired after a successful ADD (not edit) with the created row — the page
   * uses it to offer a portal invite right away. */
  onCreated?: (created: Owner) => void;
}) {
  const { toast } = useToast();
  const isEditing = !!owner;

  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrideMgmtFee, setOverrideMgmtFee] = useState(false);
  const [mgmtFeeType, setMgmtFeeType] = useState<"percent" | "flat">("percent");
  const [mgmtFeeAmount, setMgmtFeeAmount] = useState("");
  const [overrideVendorUpcharge, setOverrideVendorUpcharge] = useState(false);
  const [vendorUpchargePercent, setVendorUpchargePercent] = useState("");

  const singleOwnerQuery = useQuery<SingleOwnerResponse>({
    queryKey: [`/api/app/operations/owners/${owner?.id}`],
    enabled: isEditing && open && !!owner?.id,
  });

  const form = useForm<OwnerFormValues>({
    resolver: zodResolver(ownerFormSchema),
    defaultValues: {
      name: "",
      entityName: "",
      email: "",
      phone: "",
      mailingAddress: "",
      preferredContactMethod: "email" as any,
      notes: "",
      preApprovedThresholdCents: 0,
      portalShowBookingStats: false,
    },
  });

  // Reset the form with owner data every time the dialog opens or the selected owner changes.
  // Using defaultValues alone only fires on mount — calling form.reset() here ensures
  // every re-open for a different owner pre-fills correctly and opening for "add" clears the fields.
  useEffect(() => {
    if (!open) return;
    form.reset({
      name: owner?.name ?? "",
      entityName: owner?.entityName ?? "",
      email: owner?.email ?? "",
      phone: owner?.phone ?? "",
      mailingAddress: owner?.mailingAddress ?? "",
      preferredContactMethod: (owner?.preferredContactMethod as any) ?? "email",
      notes: owner?.notes ?? "",
      preApprovedThresholdCents: owner?.preApprovedThresholdCents ?? 0,
      portalShowBookingStats: owner?.portalShowBookingStats ?? false,
    });
    setSelectedGroupIds(initialGroupIds);
    setShowOverrides(false);
    setOverrideMgmtFee(false);
    setMgmtFeeType("percent");
    setMgmtFeeAmount("");
    setOverrideVendorUpcharge(false);
    setVendorUpchargePercent("");
  }, [open, owner?.id]);

  useEffect(() => {
    const ov = singleOwnerQuery.data?.billingOverride;
    if (!ov) return;
    if (ov.overrideMgmtFee || ov.overrideVendorUpcharge) {
      setShowOverrides(true);
      setOverrideMgmtFee(ov.overrideMgmtFee ?? false);
      setMgmtFeeType((ov.mgmtFeeType as "percent" | "flat") ?? "percent");
      setMgmtFeeAmount(ov.mgmtFeeAmount != null ? String(ov.mgmtFeeAmount) : "");
      setOverrideVendorUpcharge(ov.overrideVendorUpcharge ?? false);
      setVendorUpchargePercent(ov.vendorUpchargePercent != null ? String(ov.vendorUpchargePercent) : "");
    }
  }, [singleOwnerQuery.data?.billingOverride?.id]);

  const isSaving = form.formState.isSubmitting;

  async function handleSubmit(values: OwnerFormValues) {
    let savedOwnerId: number;
    let createdOwner: Owner | null = null;
    try {
      if (isEditing) {
        await apiRequest("PATCH", `/api/app/operations/owners/${owner.id}`, values);
        savedOwnerId = owner.id;
      } else {
        const res = await apiRequest("POST", "/api/app/operations/owners", values);
        const created = (await res.json()) as Owner;
        savedOwnerId = created.id;
        createdOwner = created;
      }
    } catch (err) {
      toast({
        title: isEditing ? "Failed to update owner" : "Failed to add owner",
        description: parseErrorMessage(err, ""),
        variant: "destructive",
      });
      return;
    }

    const prevIds = new Set(initialGroupIds);
    const nextIds = new Set(selectedGroupIds);
    const toAdd = selectedGroupIds.filter((id) => !prevIds.has(id));
    const toRemove = initialGroupIds.filter((id) => !nextIds.has(id));
    const conflictMessages: string[] = [];

    for (const groupId of toAdd) {
      try {
        await apiRequest("POST", `/api/app/operations/owners/${savedOwnerId}/assign-group`, { groupId });
      } catch (err) {
        const msg = parseErrorMessage(err, "");
        conflictMessages.push(msg || `Could not assign group #${groupId}.`);
      }
    }

    for (const groupId of toRemove) {
      try {
        await apiRequest("POST", `/api/app/operations/owners/${savedOwnerId}/unassign-group`, { groupId });
      } catch (err) {
        const msg = parseErrorMessage(err, "");
        conflictMessages.push(msg || `Could not unassign group #${groupId}.`);
      }
    }

    if (conflictMessages.length > 0) {
      toast({
        title: "Group assignment issues",
        description: conflictMessages.join(" "),
        variant: "destructive",
      });
    }

    if (overrideMgmtFee || overrideVendorUpcharge) {
      try {
        await apiRequest("PUT", `/api/app/operations/owners/${savedOwnerId}/billing-override`, {
          overrideMgmtFee,
          mgmtFeeType: overrideMgmtFee ? mgmtFeeType : null,
          mgmtFeeAmount: overrideMgmtFee && mgmtFeeAmount ? parseFloat(mgmtFeeAmount) : null,
          overrideVendorUpcharge,
          vendorUpchargePercent: overrideVendorUpcharge && vendorUpchargePercent ? parseFloat(vendorUpchargePercent) : null,
        });
      } catch (err) {
        toast({
          title: "Fee override not saved",
          description: parseErrorMessage(err, ""),
          variant: "destructive",
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
    toast({ title: isEditing ? "Owner updated" : "Owner added" });
    onClose();
    if (createdOwner) onCreated?.(createdOwner);
  }

  function toggleGroup(groupId: number, checked: boolean) {
    setSelectedGroupIds((prev) =>
      checked ? [...prev, groupId] : prev.filter((id) => id !== groupId)
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!isSaving && !v) onClose();
      }}
    >
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => { if (isSaving) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isSaving) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isSaving) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Owner" : "Add Owner"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John Smith" data-testid="input-owner-name" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="entityName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Entity / Company</FormLabel>
                  <FormControl>
                    <Input placeholder="Smith Properties LLC" data-testid="input-owner-entity" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="owner@example.com" data-testid="input-owner-email" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="(555) 000-0000" data-testid="input-owner-phone" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="mailingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mailing Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St, Atlanta, GA 30301" data-testid="input-owner-address" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="preferredContactMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Contact</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? "email"}>
                    <FormControl>
                      <SelectTrigger data-testid="select-owner-contact-method">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="all">All channels</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="preApprovedThresholdCents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pre-Approved Work Order Threshold ($)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      data-testid="input-owner-threshold"
                      {...field}
                      value={field.value ? field.value / 100 : ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? 0 : Math.round((parseFloat(e.target.value) || 0) * 100),
                        )
                      }
                    />
                  </FormControl>
                  <p className="text-xs text-gray-500">
                    Work orders above this amount require the owner's approval before proceeding.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="portalShowBookingStats"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Booking request stats in Owner Portal</FormLabel>
                    <p className="text-xs text-gray-500">
                      Adds approved/rejected counts and a trend chart to this owner's portal dashboard.
                      Counts only — rejection reasons and applicant details are never shown to owners.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                      data-testid="switch-owner-booking-stats"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder="Internal notes about this owner…"
                      data-testid="textarea-owner-notes"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                <FolderOpen className="h-4 w-4 text-gray-400" />
                Property Groups
              </p>
              {allGroups.length > 0 ? (
                <>
                  <p className="text-xs text-gray-400">
                    Assign property groups to this owner. Each group can only belong to one owner.
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {allGroups.map((group) => (
                      <div key={group.id} className="flex items-center gap-2" data-testid={`row-group-assign-${group.id}`}>
                        <Checkbox
                          id={`group-${group.id}`}
                          checked={selectedGroupIds.includes(group.id)}
                          onCheckedChange={(checked) => toggleGroup(group.id, !!checked)}
                          data-testid={`checkbox-group-${group.id}`}
                        />
                        <Label htmlFor={`group-${group.id}`} className="text-sm cursor-pointer">
                          {group.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div
                  className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-500 dark:text-gray-400 space-y-1"
                  data-testid="empty-property-groups"
                >
                  <p>
                    Property groups are how an owner is tied to their properties — you don't have any yet.
                  </p>
                  <p>
                    Create a group on the{" "}
                    <Link
                      href="/app/properties"
                      className="text-brand font-medium hover:underline"
                      data-testid="link-create-property-groups"
                    >
                      Properties page
                    </Link>
                    , add this owner's properties to it, then assign the group here.
                  </p>
                </div>
              )}
            </div>

            <Separator />
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 w-full text-left"
              onClick={() => setShowOverrides((v) => !v)}
              data-testid="button-toggle-fee-overrides"
            >
              {showOverrides ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Fee Overrides for This Owner
            </button>

            {showOverrides && (
              <div className="space-y-4 pl-1">
                <p className="text-xs text-gray-400">
                  Overrides apply only to this owner's properties and take precedence over account-level PM fee defaults.
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="override-mgmt-fee"
                      checked={overrideMgmtFee}
                      onCheckedChange={setOverrideMgmtFee}
                      data-testid="switch-override-mgmt-fee"
                    />
                    <Label htmlFor="override-mgmt-fee" className="text-sm">Override management fee</Label>
                  </div>

                  {overrideMgmtFee && (
                    <div className="grid grid-cols-2 gap-3 pl-1">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500">Fee type</Label>
                        <Select value={mgmtFeeType} onValueChange={(v) => setMgmtFeeType(v as any)}>
                          <SelectTrigger data-testid="select-override-fee-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">Percent (%)</SelectItem>
                            <SelectItem value="flat">Flat ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500">
                          {mgmtFeeType === "percent" ? "Rate (%)" : "Amount ($)"}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={mgmtFeeType === "percent" ? "10" : "500"}
                          value={mgmtFeeAmount}
                          onChange={(e) => setMgmtFeeAmount(e.target.value)}
                          data-testid="input-override-mgmt-fee-amount"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Switch
                      id="override-vendor-upcharge"
                      checked={overrideVendorUpcharge}
                      onCheckedChange={setOverrideVendorUpcharge}
                      data-testid="switch-override-vendor-upcharge"
                    />
                    <Label htmlFor="override-vendor-upcharge" className="text-sm">Override vendor upcharge</Label>
                  </div>

                  {overrideVendorUpcharge && (
                    <div className="pl-1 space-y-1.5">
                      <Label className="text-xs text-gray-500">Upcharge (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="15"
                        value={vendorUpchargePercent}
                        onChange={(e) => setVendorUpchargePercent(e.target.value)}
                        data-testid="input-override-vendor-upcharge"
                        className="max-w-40"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} data-testid="button-submit-owner-form">
                {isSaving ? "Saving…" : isEditing ? "Save Changes" : "Add Owner"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function OwnerTableRow({
  owner,
  groups,
  groupPropertiesByGroup,
  financials,
  unreadCount,
  periodLabel,
  canEdit,
  onEdit,
  onDelete,
  onRevokeAccess,
  onOpenMessages,
}: {
  owner: Owner;
  groups: PropertyGroup[];
  groupPropertiesByGroup: Record<number, GroupProperty[]>;
  financials?: OwnerFinancials;
  unreadCount: number;
  periodLabel: string;
  canEdit: boolean;
  onEdit: (owner: Owner) => void;
  onDelete: (owner: Owner) => void;
  onRevokeAccess: (owner: Owner) => void;
  onOpenMessages: (owner: Owner) => void;
}) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);

  const hasEmail = !!owner.email;
  const alreadyInvited = owner.portalStatus !== "not_invited";
  const canMessage = owner.portalStatus === "active";
  const hasPortalAccess = owner.portalStatus === "invited" || owner.portalStatus === "active";

  const inviteMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/operations/owners/${owner.id}/invite`),
    onSuccess: () => {
      queryClient.setQueriesData<OwnersResponse>({ queryKey: ["/api/app/operations/owners"] }, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          owners: prev.owners.map((o) =>
            o.id === owner.id
              ? { ...o, portalStatus: "invited" }
              : o
          ),
        };
      });
      toast({ title: alreadyInvited ? "Invite resent" : "Invite sent", description: `An invite email was sent to ${owner.email}.` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
    },
    onError: (err) => {
      toast({ title: "Failed to send invite", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const statusBadge =
    owner.portalStatus === "not_invited" ? (
      <Badge variant="secondary" className="text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800 text-xs font-medium">
        Not Invited
      </Badge>
    ) : (
      <Badge variant={(PORTAL_STATUS_COLORS[owner.portalStatus] || "secondary") as any} className="text-xs">
        {PORTAL_STATUS_LABELS[owner.portalStatus] || owner.portalStatus}
      </Badge>
    );

  return (
    <>
      <TableRow data-testid={`row-owner-${owner.id}`} className="group">
        {/* Owner name + entity */}
        <TableCell className="py-3">
          <div className="flex items-center gap-2 min-w-0">
            <UserCircle className="h-5 w-5 text-brand flex-shrink-0" />
            <div className="min-w-0">
              <TruncatedText
                as="p"
                text={owner.name}
                className="font-semibold text-gray-900 dark:text-gray-100 text-sm"
                data-testid={`text-owner-name-${owner.id}`}
              />
              {owner.entityName && (
                <TruncatedText
                  as="p"
                  text={owner.entityName}
                  className="text-xs text-gray-500 flex items-center gap-1"
                >
                  <Building2 className="h-3 w-3 flex-shrink-0" />
                  {owner.entityName}
                </TruncatedText>
              )}
            </div>
          </div>
        </TableCell>

        {/* Property groups with count + tooltip */}
        <TableCell className="py-3">
          {groups.length === 0 ? (
            <span className="text-xs text-gray-400">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              <TooltipProvider>
                {groups.map((g) => {
                  const props = groupPropertiesByGroup[g.id] ?? [];
                  const count = props.length;
                  return (
                    <Tooltip key={g.id}>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 cursor-default"
                          data-testid={`badge-group-${owner.id}-${g.id}`}
                        >
                          <FolderOpen className="h-3 w-3 text-gray-400" />
                          {g.name}
                          {count > 0 && (
                            <span className="ml-0.5 rounded-full bg-brand/10 text-brand px-1 font-medium">
                              {count}
                            </span>
                          )}
                        </span>
                      </TooltipTrigger>
                      {count > 0 && (
                        <TooltipContent side="top" className="max-w-xs" data-testid={`tooltip-group-${owner.id}-${g.id}`}>
                          <p className="font-medium mb-1 text-xs">{g.name} — {count} {count === 1 ? "property" : "properties"}</p>
                          <ul className="space-y-0.5">
                            {props.map((p) => (
                              <li key={p.id} className="flex items-center gap-1 text-xs">
                                <Home className="h-2.5 w-2.5 flex-shrink-0" />
                                <span className="truncate" title={`${p.name}${p.address ? ` · ${p.address}` : ""}`}>{p.name}{p.address ? ` · ${p.address}` : ""}</span>
                              </li>
                            ))}
                          </ul>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          )}
        </TableCell>

        {/* Pre-approved threshold */}
        <TableCell className="py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {owner.preApprovedThresholdCents > 0
            ? `$${(owner.preApprovedThresholdCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : <span className="text-gray-400">—</span>
          }
        </TableCell>

        {/* Invite status */}
        <TableCell className="py-3">
          {statusBadge}
        </TableCell>

        {/* Messaging is available only after the owner activates their portal. */}
        <TableCell className="py-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => canMessage ? onOpenMessages(owner) : inviteMutation.mutate()}
            disabled={!canMessage && (!canEdit || !hasEmail || inviteMutation.isPending)}
            title={!canMessage && !hasEmail ? "Add an email address to invite this owner" : undefined}
            className="relative h-8 px-3"
            data-testid={canMessage ? `button-messages-owner-${owner.id}` : `button-invite-owner-primary-${owner.id}`}
          >
            {canMessage ? <MessageSquare className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            <span className="ml-1.5 text-xs">
              {canMessage ? "Messages" : inviteMutation.isPending ? "Sending…" : alreadyInvited ? "Resend Invite" : "Invite"}
            </span>
            {canMessage && unreadCount > 0 && (
              <Badge
                className="ml-2 h-4 min-w-4 px-1 text-[10px] bg-red-500 hover:bg-red-500 text-white"
                data-testid={`badge-unread-owner-${owner.id}`}
              >
                {unreadCount}
              </Badge>
            )}
          </Button>
        </TableCell>

        {/* Financial expand toggle */}
        <TableCell className="py-3">
          {financials && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`button-toggle-financials-${owner.id}`}
              aria-label={expanded ? "Hide financials" : "Show financials"}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
        </TableCell>

        {/* Actions three-dots menu */}
        <TableCell className="py-3">
          {canEdit && (
            // modal={false} is deliberate: a modal dropdown sets
            // `pointer-events: none` on <body> while open, and opening a Dialog
            // from one of its items races that cleanup, leaving the body
            // permanently locked (whole page frozen until refresh). A
            // non-modal menu never touches body pointer-events, so the Edit
            // dialog opens cleanly.
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700"
                  data-testid={`button-actions-owner-${owner.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Owner actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => onEdit(owner)}
                  data-testid={`button-edit-owner-${owner.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => inviteMutation.mutate()}
                  disabled={!hasEmail || inviteMutation.isPending}
                  data-testid={`button-invite-owner-${owner.id}`}
                >
                  <Send className="h-3.5 w-3.5 mr-2" />
                  {inviteMutation.isPending ? "Sending…" : alreadyInvited ? "Resend Invite" : "Invite to Portal"}
                </DropdownMenuItem>
                {hasPortalAccess && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onRevokeAccess(owner)}
                      className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                      data-testid={`button-revoke-access-${owner.id}`}
                    >
                      <ShieldOff className="h-3.5 w-3.5 mr-2" />
                      Revoke access
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(owner)}
                  className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                  data-testid={`button-delete-owner-${owner.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Remove owner
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>

      {/* Expandable financials row */}
      {expanded && financials && (
        <TableRow data-testid={`row-owner-financials-${owner.id}`} className="bg-brand-muted/20 dark:bg-brand-muted/10">
          <TableCell colSpan={7} className="py-3 px-6">
            <div className="flex flex-wrap gap-6 text-sm" data-testid={`rollup-owner-${owner.id}`}>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">Revenue</span>
                <p className="font-semibold text-gray-800 dark:text-gray-200" data-testid={`text-owner-revenue-${owner.id}`}>
                  {formatCurrency(financials.revenue)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">Expenses</span>
                <p className="font-semibold text-gray-800 dark:text-gray-200" data-testid={`text-owner-expenses-${owner.id}`}>
                  {formatCurrency(financials.expenses)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">Net Income</span>
                <p
                  className={`font-bold ${financials.netIncome >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
                  data-testid={`text-owner-net-income-${owner.id}`}
                >
                  {formatCurrency(financials.netIncome)}
                </p>
              </div>
              <div className="text-xs text-gray-400 self-end pb-0.5" data-testid={`text-owner-period-${owner.id}`}>
                {periodLabel} · {financials.propertyCount} {financials.propertyCount === 1 ? "property" : "properties"} · {groups.length} {groups.length === 1 ? "group" : "groups"}
              </div>
              {hasPortalAccess && (
                <div className="text-xs text-gray-400 self-end pb-0.5" data-testid={`text-owner-last-login-${owner.id}`}>
                  {formatLastLogin(owner.lastLoginAt) ? `Last login: ${formatLastLogin(owner.lastLoginAt)}` : "Has not logged in yet"}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Post-add nudge: right after a new owner is created, offer to send the owner
 * portal invite in one click (same POST the row's "Invite" action uses).
 * Owners without an email can't receive an invite, so the dialog explains that
 * instead of offering the button.
 */
function InviteAfterAddDialog({ owner, onClose }: { owner: Owner | null; onClose: () => void }) {
  const { toast } = useToast();

  const inviteMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/operations/owners/${owner!.id}/invite`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
      toast({ title: "Invite sent", description: `${owner?.name} has been invited to the owner portal.` });
      onClose();
    },
    onError: (err) => {
      toast({ title: "Failed to send invite", description: parseErrorMessage(err, "Please try again from the owner's row."), variant: "destructive" });
    },
  });

  return (
    <Dialog
      open={!!owner}
      onOpenChange={(v) => { if (!inviteMutation.isPending && !v) onClose(); }}
    >
      <DialogContent
        onInteractOutside={(e) => { if (inviteMutation.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (inviteMutation.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (inviteMutation.isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Invite {owner?.name} to the Owner Portal?</DialogTitle>
        </DialogHeader>
        {owner?.email ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Send {owner.name} an email invite to set up their owner portal, where they can view
            their properties, invoices, and approvals. You can also do this later from their row
            in the directory.
          </p>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {owner?.name} doesn't have an email on file, so a portal invite can't be sent yet.
            Add their email, then use the Invite action on their row.
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={inviteMutation.isPending}
            data-testid="button-invite-later"
          >
            {owner?.email ? "Not now" : "Close"}
          </Button>
          {owner?.email && (
            <Button
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending}
              data-testid="button-invite-now"
            >
              <Send className="h-4 w-4 mr-2" />
              {inviteMutation.isPending ? "Sending…" : "Send Invite"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AppOperationsOwners() {
  const { toast } = useToast();
  const { canEdit, canView } = usePermissions();
  const [formOpen, setFormOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [editingOwnerGroupIds, setEditingOwnerGroupIds] = useState<number[]>([]);
  const [deletingOwner, setDeletingOwner] = useState<Owner | null>(null);
  const [revokingOwner, setRevokingOwner] = useState<Owner | null>(null);
  // Just-created owner awaiting the "invite to portal?" prompt (null = no prompt).
  const [invitePromptOwner, setInvitePromptOwner] = useState<Owner | null>(null);
  const [messagingOwner, setMessagingOwner] = useState<Owner | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<"name" | "status" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const dateRange = computeDateRange(datePreset, customFrom, customTo);
  const startDate = dateRange.from;
  const endDate = dateRange.to;

  const { data, isLoading: ownersLoading } = useQuery<OwnersResponse>({
    queryKey: ["/api/app/operations/owners", startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      const res = await apiRequest("GET", `/api/app/operations/owners${qs ? `?${qs}` : ""}`);
      return res.json();
    },
    enabled: canView("owners"),
  });

  const { data: groupsData, isLoading: groupsLoading } = useQuery<PropertyGroup[]>({
    queryKey: ["/api/app/property-groups"],
    enabled: canView("owners"),
  });

  const isLoading = ownersLoading || groupsLoading;

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/operations/owners/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
      toast({ title: "Owner removed" });
      setDeletingOwner(null);
    },
    onError: (err) => {
      toast({ title: "Failed to remove owner", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/app/operations/owners/${id}/portal-status`, { action: "revoke" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
      toast({ title: "Portal access revoked" });
      setRevokingOwner(null);
    },
    onError: (err) => {
      toast({ title: "Failed to revoke access", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const owners = data?.owners ?? [];
  const groupsByOwner = data?.groupsByOwner ?? {};

  function toggleSort(key: "name" | "status") {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const STATUS_SORT_ORDER: Record<string, number> = {
    not_invited: 0,
    invited: 1,
    active: 2,
    inactive: 3,
  };

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const groupFilterId = groupFilter === "all" ? null : Number(groupFilter);
  const filteredOwners = owners.filter((o) => {
    if (trimmedQuery) {
      const matchesQuery =
        String(o.name ?? "").toLowerCase().includes(trimmedQuery) ||
        String(o.entityName ?? "").toLowerCase().includes(trimmedQuery);
      if (!matchesQuery) return false;
    }
    if (statusFilter !== "all" && o.portalStatus !== statusFilter) return false;
    if (groupFilterId != null) {
      const ownerGroupIds = (groupsByOwner[o.id] ?? []).map((g) => g.id);
      if (!ownerGroupIds.includes(groupFilterId)) return false;
    }
    return true;
  });
  const hasActiveFilters = trimmedQuery !== "" || statusFilter !== "all" || groupFilterId != null;

  const displayedOwners = sortKey
    ? [...filteredOwners].sort((a, b) => {
        let cmp: number;
        if (sortKey === "name") {
          cmp = (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
        } else {
          cmp = (STATUS_SORT_ORDER[a.portalStatus] ?? 99) - (STATUS_SORT_ORDER[b.portalStatus] ?? 99);
        }
        return sortDir === "asc" ? cmp : -cmp;
      })
    : filteredOwners;
  const financialsByOwner = data?.financialsByOwner ?? {};
  const unreadByOwner = data?.unreadByOwner ?? {};
  const groupPropertiesByGroup = data?.groupPropertiesByGroup ?? {};
  const allGroups = groupsData ?? [];

  function openEdit(owner: Owner) {
    const currentGroupIds = (groupsByOwner[owner.id] ?? []).map((g) => g.id);
    setEditingOwner(owner);
    setEditingOwnerGroupIds(currentGroupIds);
    setFormOpen(true);
  }

  function openAdd() {
    setEditingOwner(null);
    setEditingOwnerGroupIds([]);
    setFormOpen(true);
  }

  return (
    <AppLayout>
      <BetaAccessGate featureKey="owner_portal" featureName="Owner Portal & CRM">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-tour="operations-owners-heading">Owners</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Manage the property owners you work with. Assign property groups and configure billing overrides per owner.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="operations-owners" />
            <GuideButton pageKey="operations-owners" />
            {canEdit("owners") && (
              <Button onClick={openAdd} data-testid="button-add-owner" data-tour="operations-owners-add">
                <Plus className="h-4 w-4 mr-2" />
                Add Owner
              </Button>
            )}
          </div>
        </div>

        <GuideNudgeBanner pageKey="operations-owners" />

        <div className="flex flex-wrap items-center gap-2" data-tour="operations-owners-period">
          <span className="text-sm text-gray-500 dark:text-gray-400">Financials period:</span>
          <DateRangeFilter
            preset={datePreset}
            customFrom={customFrom}
            customTo={customTo}
            onPresetChange={setDatePreset}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />
        </div>

        <ViewOnlyBanner area="owners" />

        {!isLoading && owners.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative max-w-sm flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by owner or entity name…"
                className="pl-9"
                data-testid="input-search-owners"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="select-filter-owner-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="not_invited">Not Invited</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {allGroups.length > 0 && (
              <Select value={groupFilter} onValueChange={setGroupFilter}>
                <SelectTrigger className="w-[180px]" data-testid="select-filter-owner-group">
                  <SelectValue placeholder="All groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All groups</SelectItem>
                  {allGroups.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)} data-testid={`option-group-${g.id}`}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                  setGroupFilter("all");
                }}
                data-testid="button-clear-owner-filters"
              >
                Clear
              </Button>
            )}
          </div>
        )}

        {isLoading ? (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 px-4 py-3">
                    <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-4 w-24 ml-auto" />
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-7 w-7 rounded" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : owners.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <UserCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="font-medium text-gray-700 dark:text-gray-300">No owners yet</p>
              <p className="text-sm text-gray-400 mt-1">Add your first owner to assign property groups and configure billing.</p>
              {canEdit("owners") && (
                <Button
                  className="mt-4"
                  onClick={openAdd}
                  data-testid="button-add-first-owner"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Owner
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card data-tour="operations-owners-grid">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-4 w-[220px]">
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                        data-testid="button-sort-owner-name"
                      >
                        Owner
                        {sortKey === "name" ? (
                          sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Property Groups</TableHead>
                    <TableHead className="w-[140px]">Pre-Approved</TableHead>
                    <TableHead className="w-[120px]">
                      <button
                        type="button"
                        onClick={() => toggleSort("status")}
                        className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                        data-testid="button-sort-owner-status"
                      >
                        Status
                        {sortKey === "status" ? (
                          sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-[130px]">Messages</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead className="w-[48px] pr-4"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedOwners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10 text-sm text-gray-400" data-testid="text-no-owners-match">
                        {trimmedQuery
                          ? `No owners match "${searchQuery.trim()}".`
                          : "No owners match the selected filters."}
                      </TableCell>
                    </TableRow>
                  ) : displayedOwners.map((owner) => (
                    <OwnerTableRow
                      key={owner.id}
                      owner={owner}
                      groups={groupsByOwner[owner.id] ?? []}
                      groupPropertiesByGroup={groupPropertiesByGroup}
                      financials={financialsByOwner[owner.id]}
                      unreadCount={unreadByOwner[owner.id] ?? 0}
                      periodLabel={getPresetLabel(datePreset)}
                      canEdit={canEdit("owners")}
                      onEdit={openEdit}
                      onDelete={setDeletingOwner}
                      onRevokeAccess={setRevokingOwner}
                      onOpenMessages={setMessagingOwner}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <OwnerForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingOwner(null); setEditingOwnerGroupIds([]); }}
        owner={editingOwner}
        allGroups={allGroups}
        initialGroupIds={editingOwnerGroupIds}
        onCreated={(created) => setInvitePromptOwner(created)}
      />

      <InviteAfterAddDialog
        owner={invitePromptOwner}
        onClose={() => setInvitePromptOwner(null)}
      />

      <Dialog
        open={!!deletingOwner}
        onOpenChange={(v) => { if (!deleteMutation.isPending && !v) setDeletingOwner(null); }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (deleteMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (deleteMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (deleteMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Remove Owner</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to remove <strong>{deletingOwner?.name}</strong>? Their assigned property groups will be unlinked but not deleted.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingOwner(null)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deletingOwner && deleteMutation.mutate(deletingOwner.id)}
              data-testid="button-confirm-delete-owner"
            >
              {deleteMutation.isPending ? "Removing…" : "Remove Owner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!revokingOwner}
        onOpenChange={(v) => { if (!revokeMutation.isPending && !v) setRevokingOwner(null); }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (revokeMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (revokeMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (revokeMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Revoke Portal Access</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Revoke owner portal access for <strong>{revokingOwner?.name}</strong>? Their password and any pending setup link will be cleared and they'll be reset to "Not Invited". You can send a fresh invite later.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokingOwner(null)} disabled={revokeMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revokeMutation.isPending}
              onClick={() => revokingOwner && revokeMutation.mutate(revokingOwner.id)}
              data-testid="button-confirm-revoke-access"
            >
              {revokeMutation.isPending ? "Revoking…" : "Revoke Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OwnerMessagesDialog
        owner={messagingOwner}
        open={!!messagingOwner}
        onClose={() => setMessagingOwner(null)}
      />
      </BetaAccessGate>
    </AppLayout>
  );
}
